// MACHINERY V2 (Stroje) — Prehľad: POSLEDNÉ UDALOSTI + the Midas CTA (M2.16d,
// board 20 lower half).
//
// Board 20's row is: pip · text · mono time, with a hairline between rows and
// NO pip on an informational row (machinery-mobile.jsx:590-597 renders exactly
// that, passing `null` for the middle row's dot). The pip is a SEVERITY, so an
// event the model cannot grade gets a spacer, never a grey dot standing in for
// one — see prehladEvents.ts `eventPip`.
//
// SR ROWS ARE ACTIVATABLE, GATEWAY ROWS ARE NOT. A service report has a detail
// page; a gateway event has nowhere to go. The row therefore becomes a real
// <button> only when it carries `onOpen` — NOT a div with role="button".
//
// THAT CHOICE IS THE EVENT-PATH RULE, NOT A STYLE PREFERENCE. A native button
// gets Enter and Space for free, because the browser SYNTHESISES a click from
// them, so one `onClick` covers the pointer path and both keyboard paths. That
// is also why there is deliberately no hand-rolled `onKeyDown` here: the only
// thing such a handler could add is a `preventDefault`, which would suppress the
// very click it is meant to help. The row contains no nested control either, so
// there is nothing to stopPropagation for. (Five prior defects of this shape in
// this module, four of them on CapabilityBadge — the cheapest way not to have a
// sixth is to not hand-roll the mechanism.)
//
// >>> THE MIDAS CTA USES THE BRAND ACCENT, NEVER THE MODULE ACCENT. <<<
// `--accent` / `--accent-soft` / `--accent-line`, exactly as the mobile
// README's Profil note requires ("brand accent, not module accent — Midas is
// global") and exactly as chrome/ChromeNavV4.tsx:170-193 already paints the nav's
// Midas button. `--mod-accent` is Stroje chrome; Midas is reachable from every
// module and is never tinted by the module it was launched from.
//
// ITS GATE IS THE NAV'S GATE, NOT A NEW ONE. `isBotEnabled && canSeeModule(
// Modules.Midas)` — the same pair ChromeNavV4.tsx:73 and MobileDrawer.tsx:161
// use. /midas sits behind MidasRouteGuard, so an ungated CTA would hand a
// Midas-less user a button that bounces them straight back.
//
// IT NAVIGATES TO /midas WITH NO MACHINE PARAMETER, ON PURPOSE. Nothing under
// customer/midas/ reads a query parameter (grep-verified: no useSearchParams,
// no searchParams.get in that folder), so a `?machineId=` would be a parameter
// no consumer reads — silently dropped, and read by the next maintainer as a
// working feature. Machine-scoped prefill is filed as a MIDAS-side ask; the
// label stays honest until it lands.
//
// No Tailwind, no shadcn, no MUI, no @emotion, no sx, no dt tokens.
import React from "react";
import { useNavigate } from "react-router-dom";

import { Modules } from "../../../../../core/models/PermissionModels";
import { useAppSelector } from "../../../../../shared/hooks/sharedHooks";
import useTranslation from "../../../../../shared/hooks/useTranslation";
import { canSeeModule } from "../../chrome/navModules";
import { PrehladRow, prehladTimeLabel } from "./prehladEvents";

const EventRow: React.FC<{ row: PrehladRow; timeLabel: string }> = ({ row, timeLabel }) => {
  const activatable = !!row.onOpen;
  const body = (
    <React.Fragment>
      {row.pip ? (
        <span className={`pip pip-${row.pip}`} style={{ flexShrink: 0, alignSelf: "center" }} />
      ) : (
        // Board 20's un-graded row: the pip's width is reserved so the text of
        // every row still starts on one vertical line.
        <span aria-hidden style={{ width: 8, flexShrink: 0 }} />
      )}
      <span
        style={{
          fontSize: 12,
          color: "var(--ink-2)",
          flex: 1,
          minWidth: 0,
          overflow: "hidden",
          textOverflow: "ellipsis",
          whiteSpace: "nowrap",
          textAlign: "left",
        }}
      >
        {row.text}
      </span>
      <span
        style={{
          fontFamily: "var(--font-mono)",
          fontSize: 9.5,
          color: "var(--faint)",
          flexShrink: 0,
        }}
      >
        {timeLabel}
      </span>
    </React.Fragment>
  );

  const shared: React.CSSProperties = {
    display: "flex",
    alignItems: "center",
    gap: 8,
    padding: "8px 0",
    borderTop: "1px solid var(--border-soft)",
    minWidth: 0,
    width: "100%",
  };

  if (!activatable) {
    return (
      <div className="mv2-prehlad-row" style={shared}>
        {body}
      </div>
    );
  }

  return (
    <button
      type="button"
      className="mv2-prehlad-row"
      onClick={row.onOpen}
      style={{
        ...shared,
        border: 0,
        borderTop: "1px solid var(--border-soft)",
        background: "transparent",
        font: "inherit",
        cursor: "pointer",
        textAlign: "left",
      }}
    >
      {body}
    </button>
  );
};

export interface PrehladEventListProps {
  rows: PrehladRow[];
  /** Rendered instead of the rows when there is nothing at all to show. */
  emptyText: string;
}

const PrehladEventList: React.FC<PrehladEventListProps> = ({ rows, emptyText }) => {
  const { translate } = useTranslation("myMachinery");
  const navigate = useNavigate();
  const isBotEnabled = useAppSelector((s) => s.chatBot.isBotEnabled);
  const canMidas = isBotEnabled && canSeeModule(Modules.Midas);

  // ONE clock for the whole list. Reading `new Date()` inside the map would let
  // two rows straddle midnight and disagree about which of them was "yesterday".
  const now = new Date();
  const yesterday = translate("myMachinery:v2.profile.overview.yesterday");

  return (
    <React.Fragment>
      <section
        style={{
          background: "var(--card)",
          border: "1px solid var(--border)",
          borderRadius: 10,
          boxShadow: "var(--shadow)",
          padding: "11px 13px",
          minWidth: 0,
        }}
      >
        <div className="eyebrow" style={{ paddingBottom: 6 }}>
          {translate("myMachinery:v2.profile.overview.recentEvents")}
        </div>
        {rows.length === 0 ? (
          <div style={{ fontSize: 12.5, color: "var(--muted)", padding: "4px 0 2px" }}>
            {emptyText}
          </div>
        ) : (
          rows.map((row) => (
            <EventRow
              key={row.key}
              row={row}
              timeLabel={prehladTimeLabel(row.at, yesterday, now)}
            />
          ))
        )}
      </section>

      {canMidas && (
        <button
          type="button"
          onClick={() => navigate("/midas")}
          style={{
            // >= 44px on BOTH axes: the height is explicit and minWidth
            // accompanies it, even though the button is full-width — the pair is
            // the law, and a later `minWidth: 0` in this object would silently
            // void it, so there is none.
            height: 44,
            minWidth: 44,
            width: "100%",
            borderRadius: 10,
            display: "inline-flex",
            alignItems: "center",
            justifyContent: "center",
            gap: 8,
            cursor: "pointer",
            fontFamily: "inherit",
            fontSize: 13,
            fontWeight: 600,
            // BRAND accent — see the header. Never var(--mod-accent).
            color: "var(--accent)",
            background: "var(--accent-soft)",
            border: "1px solid var(--accent-line)",
          }}
        >
          <span aria-hidden>✦</span>
          {translate("myMachinery:v2.profile.overview.askMidas")}
        </button>
      )}
    </React.Fragment>
  );
};

export default PrehladEventList;
